import React, { useCallback, useState } from "react";
import { Tooltip, styled } from "@mui/material";
import AgentAvatar from "../pages/AgentAvatar";
import { useContextAgent } from "./ContextAgentContext";
import useRegisteredAgentProfile from "./useRegisteredAgentProfile";
import "./ContextAgentSidebarTab.css";

const TAB_WIDTH = 34;
const TAB_HEIGHT = 56;
const TAB_RADIUS = 6;
const TAB_BORDER_RADIUS = `0 ${TAB_RADIUS}px ${TAB_RADIUS}px 0`;
const AVATAR_SIZE = 22;

const PULL_OUT_ANIMATION = "context-agent-tab-pull-out";
const PULL_IN_ANIMATION = "context-agent-tab-pull-in";

const TabBody = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  boxSizing: "border-box",
  backgroundColor: theme.palette.background.paper,
  borderStyle: "solid",
  borderWidth: "1px 1px 1px 0",
  borderColor: theme.palette.divider,
  borderRadius: TAB_BORDER_RADIUS,
}));

const AgentTabRoot = styled("button")(({ theme }) => ({
  position: "absolute",
  left: 0,
  top: "50%",
  zIndex: 1200,
  height: TAB_HEIGHT,
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  border: "none",
  background: "transparent",
  pointerEvents: "auto",
  overflow: "hidden",
  borderRadius: TAB_BORDER_RADIUS,
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

/** Sidebar seam tab — paper lip with a square system-agent avatar inside. */
export default function ContextAgentSidebarTab() {
  const { registration, open, toggleOpen, isRegistered } = useContextAgent();
  const [isExiting, setIsExiting] = useState(false);
  const { displayName, avatarUrl } = useRegisteredAgentProfile(registration?.agentName);

  const handleClick = useCallback(() => {
    if (isExiting) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      toggleOpen();
      return;
    }
    setIsExiting(true);
  }, [isExiting, toggleOpen]);

  const handleAnimationEnd = useCallback(
    (event) => {
      if (event.currentTarget !== event.target) {
        return;
      }

      const { animationName } = event;
      if (animationName === PULL_IN_ANIMATION && isExiting) {
        setIsExiting(false);
        toggleOpen();
        return;
      }

      if (animationName === PULL_OUT_ANIMATION && !isExiting) {
        event.currentTarget.classList.remove("context-agent-sidebar-tab--enter");
      }
    },
    [isExiting, toggleOpen]
  );

  if (!isRegistered || !registration) {
    return null;
  }

  if (open && !isExiting) {
    return null;
  }

  const { agentName, scopeLabel } = registration;
  const tooltip = scopeLabel ? `${agentName} · ${scopeLabel}` : agentName;

  const tabClassName = isExiting
    ? "context-agent-sidebar-tab--exit"
    : "context-agent-sidebar-tab--enter";

  return (
    <Tooltip title={tooltip} placement="right" arrow>
      <AgentTabRoot
        type="button"
        className={`context-agent-sidebar-tab ${tabClassName}`}
        style={{ "--context-agent-tab-width": `${TAB_WIDTH}px` }}
        aria-label={`Open ${agentName}`}
        aria-pressed={false}
        onClick={handleClick}
        onAnimationEnd={handleAnimationEnd}
        data-testid="context-agent-sidebar-tab"
      >
        <TabBody>
          <AgentAvatar
            name={displayName}
            src={avatarUrl}
            size={AVATAR_SIZE}
            fontSize="8px"
            systemAgent
            sx={{
              lineHeight: 1,
              letterSpacing: "-0.04em",
            }}
          />
        </TabBody>
      </AgentTabRoot>
    </Tooltip>
  );
}
